import { Button, Modal, Stack, Text, Textarea } from "@assetwatch/ui";
import { useEffect, useState } from "react";
import {
  type BulkActionOutcome,
  useBulkCancelWorkOrdersMutation,
} from "../../queries/useWorkOrderBulkActionsMutations";
import type { WorkOrder } from "../../queries/useWorkOrdersQuery";
import { displayWorkOrderNumber } from "../snowflakes/work-orders/presentation";
import {
  cancellationIneligibilityReason,
  isEligibleForCancellation,
} from "./bulkEligibility";

type Step = "review" | "results";

export function BulkDeleteWorkOrdersModal({
  open,
  onOpenChange,
  workOrders,
  onOutcome,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrders: WorkOrder[];
  onOutcome: (outcome: BulkActionOutcome) => void;
}) {
  const mutation = useBulkCancelWorkOrdersMutation();
  const [step, setStep] = useState<Step>("review");
  const [reason, setReason] = useState("");
  const [showValidationError, setShowValidationError] = useState(false);
  const [outcome, setOutcome] = useState<BulkActionOutcome | null>(null);

  const eligible = workOrders.filter(isEligibleForCancellation);
  const ineligible = workOrders.filter(
    (workOrder) => !isEligibleForCancellation(workOrder),
  );

  useEffect(() => {
    if (open) {
      setStep("review");
      setReason("");
      setShowValidationError(false);
      setOutcome(null);
      mutation.reset();
    }
  }, [open, mutation.reset]);

  async function confirm() {
    if (!reason.trim()) {
      setShowValidationError(true);
      return;
    }
    try {
      const result = await mutation.mutateAsync({
        targets: eligible.map((workOrder) => ({
          id: workOrder.id,
          number: workOrder.number,
        })),
        cancellationReason: reason.trim(),
      });
      setOutcome(result);
      onOutcome(result);
      setStep("results");
    } catch {
      // mutation.isError drives the inline error message; stay on the review step.
    }
  }

  return (
    <Modal
      onOpenChange={onOpenChange}
      open={open}
      size="md"
      title={
        step === "review" ? "Delete Work Orders" : "Delete Work Orders results"
      }
    >
      {step === "review" ? (
        <Stack gap={16}>
          <Text>
            Canceling {eligible.length} of {workOrders.length} selected Work
            Orders stops execution and declines any linked Approved Requests.
          </Text>
          {ineligible.length > 0 ? (
            <Stack gap={8}>
              <Text size="sm" tone="subtle">
                Not eligible
              </Text>
              {ineligible.map((workOrder) => (
                <div
                  className="flex items-center justify-between gap-(--spacing-8) rounded-(--radius-sm) border border-outline-subtle px-(--spacing-12) py-(--spacing-8)"
                  key={workOrder.id}
                >
                  <Text size="sm">
                    {displayWorkOrderNumber(workOrder.number)} —{" "}
                    {workOrder.title}
                  </Text>
                  <Text size="sm" tone="subtle">
                    {cancellationIneligibilityReason()}
                  </Text>
                </div>
              ))}
            </Stack>
          ) : null}
          {eligible.length > 0 ? (
            <Textarea
              aria-label="Cancellation reason"
              label="Cancellation reason"
              onChange={(event) => {
                setReason(event.target.value);
                setShowValidationError(false);
              }}
              value={reason}
            />
          ) : null}
          {showValidationError ? (
            <Text tone="negative">A cancellation reason is required.</Text>
          ) : mutation.isError ? (
            <Text tone="negative">
              Unable to cancel the selected Work Orders. Try again.
            </Text>
          ) : null}
          <div className="flex justify-end gap-(--spacing-8)">
            <Button onClick={() => onOpenChange(false)} variant="outline">
              Cancel
            </Button>
            <Button
              disabled={eligible.length === 0 || mutation.isPending}
              loading={mutation.isPending}
              loadingText="Deleting"
              onClick={() => void confirm()}
            >
              Delete Work Orders
            </Button>
          </div>
        </Stack>
      ) : (
        <Stack gap={16}>
          <Text>
            {outcome?.succeeded.length ?? 0} of{" "}
            {(outcome?.succeeded.length ?? 0) + (outcome?.failed.length ?? 0)}{" "}
            Work Orders were canceled.
          </Text>
          {outcome && outcome.failed.length > 0 ? (
            <Stack gap={8}>
              <Text size="sm" tone="negative">
                Could not be canceled
              </Text>
              {outcome.failed.map((item) => (
                <div
                  className="flex items-center justify-between gap-(--spacing-8) rounded-(--radius-sm) border border-outline-subtle px-(--spacing-12) py-(--spacing-8)"
                  key={item.id}
                >
                  <Text size="sm">{displayWorkOrderNumber(item.number)}</Text>
                  <Text size="sm" tone="negative">
                    {item.reason}
                  </Text>
                </div>
              ))}
            </Stack>
          ) : null}
          <div className="flex justify-end gap-(--spacing-8)">
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </div>
        </Stack>
      )}
    </Modal>
  );
}
